import axios from '@/libs/axios'
import AuthService from './auth-header'

// /*dev*/const API_URL = 'https://ats.sattvahuman.in/api/';
// /*prod*/ // const API_URL = 'https://ats.sattvahuman.in/api/';

let API_URL = ''
if (process.env.NODE_ENV === "production") {
  API_URL = 'https://ats.sattvahuman.in/api/';
} else {
  API_URL = 'http://192.168.0.30:5000/api/';
}

function extractApiErrorMessage(err, fallback) {
  const data = err.response?.data
  if (!data) return err.message || fallback

  if (typeof data.message === 'string' && data.message.trim()) {
    return data.message
  }

  const nested = data.error?.errors?.[0]?.message || data.errors?.[0]?.message
  if (nested) return nested

  if (typeof data.error === 'string' && data.error.trim()) {
    return data.error
  }

  return err.message || fallback
}

/** Normalize offer-table-config list payloads (array / data / rows / nested). */
function extractOfferConfigRows(payload) {
  if (!payload) return []
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload.data)) return payload.data
  if (Array.isArray(payload.rows)) return payload.rows
  if (payload.data && Array.isArray(payload.data.data)) return payload.data.data
  if (payload.data && Array.isArray(payload.data.rows)) return payload.data.rows
  return []
}

class SiteConfigService {
  // List offer-table configs (admin). Pass is_active true/false to filter; omit to fetch all.
  async getAllOfferConfigs(options = {}) {
    try {
      const params = {}
      if (options.is_active !== undefined) {
        params.is_active = options.is_active
      }
      if (options.client) {
        params.client = options.client
      }
      if (options.site_code) {
        params.site_code = options.site_code
      }

      const res = await axios.get(API_URL + 'offer-table-config', {
        params,
        headers: AuthService.authHeader(),
      })

      const rows = extractOfferConfigRows(res.data)
      if (res.data && (res.data.status === 'OK' || res.data.status === 'ok')) {
        return { status: 'OK', message: res.data.message || 'Configs retrieved', data: rows }
      }
      return { status: 'OK', message: 'Configs retrieved', data: rows }
    } catch (err) {
      console.error('Error fetching offer configs:', err)
      return {
        status: 'FAILED',
        message: err.response?.data?.message || 'Failed to fetch offer configurations',
        data: [],
      }
    }
  }

  // Get all active site configurations
  // Backend endpoint: GET /api/offer-table-config?is_active=true[&client=...]
  // Note: backend filters client with ILIKE '%client%' — value must match offer_table_configs.client
  async getAllSites(options = {}) {
    try {
      const apiUrl = API_URL + 'offer-table-config'
      const params = { is_active: true }
      if (options.client) {
        params.client = options.client
      }
      console.log('🔍 [Site Config Service] Fetching sites from database:', apiUrl)
      console.log('🔍 [Site Config Service] Query params:', params)

      const res = await axios.get(apiUrl, {
        params,
        headers: AuthService.authHeader()
      })

      const rows = extractOfferConfigRows(res.data)
      console.log('✅ [Site Config Service] API Response received:', {
        status: res.data?.status,
        dataCount: rows.length,
        clientFilter: options.client || null,
      })

      if (res.data && (res.data.status === 'OK' || res.data.status === 'ok')) {
        return { status: 'OK', message: res.data.message || 'Sites retrieved', data: rows }
      }

      // Handle unexpected response format (still try to extract rows)
      console.warn('⚠️ [Site Config Service] Unexpected response format, attempting to parse')
      return { status: 'OK', message: 'Sites retrieved', data: rows }
    } catch (err) {
      console.error('❌ [Site Config Service] Error fetching all sites from database:', err)
      console.error('❌ [Site Config Service] Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      })
      return {
        status: 'FAILED',
        message: err.response?.data?.message || 'Failed to fetch sites',
        data: []
      }
    }
  }

  // Get site configuration by site code
  // Backend endpoint: GET /api/offer-table-config/site/:site_code
  // Returns array of configs (can have multiple configs per site_code)
  async getSiteByCode(siteCode) {
    try {
      const res = await axios.get(API_URL + `offer-table-config/site/${siteCode}`, {
        headers: AuthService.authHeader()
      })

      // Backend returns: { status: "OK", message: "...", data: [...] }
      if (res.data && res.data.status === 'OK' && res.data.data) {
        // If multiple configs exist, return the most recent one (first in DESC order)
        const configs = Array.isArray(res.data.data) ? res.data.data : [res.data.data]
        if (configs.length > 0) {
          // Return the first one (most recent due to DESC order in backend)
          return {
            status: 'OK',
            message: res.data.message || 'Site configuration retrieved',
            data: configs[0]
          }
        }
        return {
          status: 'FAILED',
          message: `No configuration found for site_code=${siteCode}`,
          data: null
        }
      }

      // Unexpected response format
      return {
        status: 'FAILED',
        message: 'Unexpected response format from server',
        data: null
      }
    } catch (err) {
      console.error(`Error fetching site ${siteCode}:`, err)
      // Handle 404 specifically
      if (err.response?.status === 404) {
        return {
          status: 'FAILED',
          message: `Site configuration not found for site_code=${siteCode}`,
          data: null
        }
      }
      return {
        status: 'FAILED',
        message: err.response?.data?.message || 'Failed to fetch site configuration',
        data: null
      }
    }
  }

  // Get all site configurations by site code (returns all configs, not just the first)
  // Backend endpoint: GET /api/offer-table-config/site/:site_code
  // Returns all configs for the site (useful for extracting job titles)
  async getAllSiteConfigsByCode(siteCode) {
    try {
      const res = await axios.get(API_URL + `offer-table-config/site/${siteCode}`, {
        headers: AuthService.authHeader()
      })

      // Backend returns: { status: "OK", message: "...", data: [...] }
      if (res.data && res.data.status === 'OK' && res.data.data) {
        // Return all configs as an array
        const configs = Array.isArray(res.data.data) ? res.data.data : [res.data.data]
        return {
          status: 'OK',
          message: res.data.message || 'Site configurations retrieved',
          data: configs
        }
      }

      // Unexpected response format
      return {
        status: 'FAILED',
        message: 'Unexpected response format from server',
        data: []
      }
    } catch (err) {
      console.error(`Error fetching all site configs for ${siteCode}:`, err)
      // Handle 404 specifically
      if (err.response?.status === 404) {
        return {
          status: 'FAILED',
          message: `Site configurations not found for site_code=${siteCode}`,
          data: []
        }
      }
      return {
        status: 'FAILED',
        message: err.response?.data?.message || 'Failed to fetch site configurations',
        data: []
      }
    }
  }

  // Get site configuration by site code and job title
  // Backend endpoint: GET /api/offer-table-config/site/:site_code
  // Filters by jobTitle in configuration column
  async getSiteByCodeAndJobTitle(siteCode, jobTitle) {
    try {
      const res = await axios.get(API_URL + `offer-table-config/site/${siteCode}`, {
        headers: AuthService.authHeader()
      })

      // Backend returns: { status: "OK", message: "...", data: [...] }
      if (res.data && res.data.status === 'OK' && res.data.data) {
        const configs = Array.isArray(res.data.data) ? res.data.data : [res.data.data]

        // Find config matching the jobTitle
        for (const config of configs) {
          if (config.configuration) {
            try {
              let configData = config.configuration
              if (typeof configData === 'string') {
                configData = JSON.parse(configData)
              }

              if (configData && String(configData.jobTitle || '').trim().toLowerCase()
                === String(jobTitle || '').trim().toLowerCase()) {
                return {
                  status: 'OK',
                  message: res.data.message || 'Site configuration retrieved',
                  data: config
                }
              }
            } catch (error) {
              console.error('Error parsing configuration:', error)
            }
          }
        }

        return {
          status: 'FAILED',
          message: `No configuration found for site_code=${siteCode} with jobTitle=${jobTitle}`,
          data: null
        }
      }

      return {
        status: 'FAILED',
        message: 'Unexpected response format from server',
        data: null
      }
    } catch (err) {
      console.error(`Error fetching site ${siteCode} with jobTitle ${jobTitle}:`, err)
      if (err.response?.status === 404) {
        return {
          status: 'FAILED',
          message: `Site configuration not found for site_code=${siteCode}`,
          data: null
        }
      }
      return {
        status: 'FAILED',
        message: err.response?.data?.message || 'Failed to fetch site configuration',
        data: null
      }
    }
  }

  // Create a new site configuration
  // Backend endpoint: POST /api/offer-table-config
  async createSite(siteData) {
    try {
      const res = await axios.post(API_URL + 'offer-table-config', siteData, {
        headers: AuthService.authHeader()
      })

      // Backend returns: { status: "OK", message: "...", data: {...} }
      const body = res.data
      if (body && body.status && body.status !== 'OK') {
        return {
          status: 'FAILED',
          message: body.message || 'Failed to create site configuration',
          data: body.data || null,
        }
      }
      return body
    } catch (err) {
      const status = err.response?.status
      let message = extractApiErrorMessage(err, 'Failed to create site configuration')
      if (
        status === 413
        || /entity too large|payload too large|request entity too large/i.test(message)
      ) {
        message = 'Request too large (limit 2 MB). Remove or compress embedded images (logo, header, watermark) and try again.'
      } else if (/id must be unique|unique constraint|duplicate key/i.test(message)) {
        message = 'Server database ID sequence is out of sync — contact admin to reset offer_table_configs_id_seq.'
      }
     if (process.env.NODE_ENV === "production") {
        console.error('Error creating site:', {
          status,
          message,
          response: err.response?.data,
        })
      } else {
        console.error('Error creating site:', message)
      }
      return {
        status: 'FAILED',
        message,
        data: null
      }
    }
  }

  // Update site configuration by ID
  // Backend endpoint: PUT /api/offer-table-config/:id
  async updateSiteById(id, siteData) {
    if (id == null || id === '') {
      return {
        status: 'FAILED',
        message: 'Cannot update offer template: missing id',
        data: null,
      }
    }
    try {
      const res = await axios.put(API_URL + `offer-table-config/${id}`, siteData, {
        headers: AuthService.authHeader()
      })

      // Backend returns: { status: "OK", message: "...", data: {...} }
      const body = res.data
      if (body && body.status && body.status !== 'OK') {
        return {
          status: 'FAILED',
          message: body.message || `Failed to update offer template id=${id}`,
          data: body.data || null,
        }
      }
      return body
    } catch (err) {
      const status = err.response?.status
      let message = extractApiErrorMessage(err, `Failed to update offer template id=${id}`)
      if (status === 404) {
        message = err.response?.data?.message
          || `Offer template id=${id} was not found. It may have been deleted — refresh the list.`
      } else if (
        status === 413
        || /entity too large|payload too large|request entity too large/i.test(message)
      ) {
        message = 'Request too large (limit 2 MB). Remove or compress embedded images (logo, header, watermark) and try again.'
      }
     if (process.env.NODE_ENV === "production") {
        console.error('Error updating site:', { id, status, message, response: err.response?.data })
      } else {
        console.error('Error updating site:', id, message)
      }
      return {
        status: 'FAILED',
        message,
        data: null
      }
    }
  }

  // Update site configuration by site code
  // Backend endpoint: PUT /api/offer-table-config/site/:site_code
  // Updates all configs with the given site_code
  async updateSiteByCode(siteCode, siteData) {
    try {
      const res = await axios.put(API_URL + `offer-table-config/site/${siteCode}`, siteData, {
        headers: AuthService.authHeader()
      })

      // Backend returns: { status: "OK", message: "...", data: [...] }
      return res.data
    } catch (err) {
      console.error('Error updating site by code:', err)
      return {
        status: 'FAILED',
        message: err.response?.data?.message || 'Failed to update site configuration',
        data: null
      }
    }
  }

  // Delete site configuration by ID
  // Backend endpoint: DELETE /api/offer-table-config/:id
  async deleteSiteById(id) {
    try {
      const res = await axios.delete(API_URL + `offer-table-config/${id}`, {
        headers: AuthService.authHeader()
      })

      // Backend returns: { status: "OK", message: "..." }
      return res.data
    } catch (err) {
      console.error('Error deleting site:', err)
      return {
        status: 'FAILED',
        message: err.response?.data?.message || 'Failed to delete site configuration'
      }
    }
  }

  // Get site configuration by ID
  // Backend endpoint: GET /api/offer-table-config/:id
  async getSiteById(id) {
    try {
      const res = await axios.get(API_URL + `offer-table-config/${id}`, {
        headers: AuthService.authHeader()
      })

      // Backend returns: { status: "OK", message: "...", data: {...} }
      return res.data
    } catch (err) {
      console.error(`Error fetching site by id ${id}:`, err)
      return {
        status: 'FAILED',
        message: err.response?.data?.message || 'Failed to fetch site configuration',
        data: null
      }
    }
  }
}

export default new SiteConfigService()